import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Users as UsersIcon, Shield, Lock, Unlock } from "lucide-react";
import { ConfirmModal, BTN_PRIMARY } from "components/admin/AdminUI";
import { useAuth } from "contexts/AuthContext";
import api from "lib/api";

function nameOf(u) {
  const n = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return n || u.email;
}

function StatusBadges({ u }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {u.is_admin && (
        <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
          <Shield className="h-3 w-3" /> Admin
        </span>
      )}
      {!u.is_active && (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
          Inativo
        </span>
      )}
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users/");
      setUsers(data.results || data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const isSelf = (u) => u.user_id === user?.user_id || u.email === user?.email;

  async function confirmDelete() {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${deleting.user_id}/`);
      setDeleting(null);
      fetchUsers();
    } finally { setBusy(false); }
  }
  async function confirmToggle() {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${toggling.user_id}/`, { is_active: !toggling.is_active });
      setToggling(null);
      fetchUsers();
    } finally { setBusy(false); }
  }

  const Actions = ({ u }) => (
    <div className="flex items-center gap-1">
      <button onClick={() => navigate(`/admin/users/${u.user_id}/edit`)}
        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" title="Editar">
        <Pencil className="h-4 w-4" />
      </button>
      {!isSelf(u) && (
        <button onClick={() => setToggling(u)}
          className={`rounded p-1.5 text-muted-foreground transition-colors ${u.is_active ? "hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20" : "hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-900/20"}`}
          title={u.is_active ? "Desativar" : "Ativar"}>
          {u.is_active ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
        </button>
      )}
      {!isSelf(u) && (
        <button onClick={() => setDeleting(u)}
          className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20" title="Excluir">
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  return (
    <div>
      <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Usuários</h1>
          <p className="mt-1 text-sm text-muted-foreground">Gerencie os usuários cadastrados no sistema.</p>
        </div>
        <button onClick={() => navigate("/admin/users/new")} className={BTN_PRIMARY}>
          <Plus className="h-4 w-4" /> Novo Usuário
        </button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div className="flex justify-center py-12"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" /></div>
        ) : users.length === 0 ? (
          <div className="py-12 text-center">
            <UsersIcon className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Nenhum usuário cadastrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-border transition-colors last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">{nameOf(u)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3"><StatusBadges u={u} /></td>
                    <td className="px-4 py-3"><div className="flex justify-end"><Actions u={u} /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleting && (
        <ConfirmModal icon={Trash2} iconClass="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
          title="Excluir usuário?" confirmLabel="Excluir" confirmClass="bg-red-600 text-white hover:bg-red-700"
          loading={busy} onClose={() => setDeleting(null)} onConfirm={confirmDelete}>
          Tem certeza que deseja excluir <strong>{nameOf(deleting)}</strong>?
        </ConfirmModal>
      )}
      {toggling && (
        <ConfirmModal icon={toggling.is_active ? Lock : Unlock}
          iconClass={toggling.is_active ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400" : "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"}
          title={toggling.is_active ? "Desativar usuário?" : "Ativar usuário?"}
          confirmLabel={toggling.is_active ? "Desativar" : "Ativar"}
          confirmClass={toggling.is_active ? "bg-red-600 text-white hover:bg-red-700" : "bg-emerald-600 text-white hover:bg-emerald-700"}
          loading={busy} onClose={() => setToggling(null)} onConfirm={confirmToggle}>
          {toggling.is_active ? "O usuário não poderá mais acessar o sistema." : "O usuário poderá acessar o sistema novamente."}
        </ConfirmModal>
      )}
    </div>
  );
}
